import { ApiResponse } from "src/models";
import { LetterEmailStatus } from "src/models/sessions";
import { LONG_REQUEST_TIMEOUT_MS, apiSlice } from "../../apiSlice";

export type DocumentType =
  | "certificate"
  | "certificate_batch"
  | "family_letter"
  | "class_scorecard"
  | "export_bundle";
export interface SessionDocument {
  id: string;
  type: DocumentType;
  sessionId: string | null;
  studentId: string | null;
  tier: "presidential" | "national" | "participation" | "incomplete" | null;
  generatedAt: string;
  supersededAt: string | null;
  sentAt: string | null;
  /** Presigned URL valid for 15 minutes. */
  url: string;
}

/** `GET v1/sessions/:id/documents`: the documents and whether letters can be emailed. */
export interface SessionDocumentsView {
  documents: SessionDocument[];
  /** Null when the server did not say; the console then does not offer email. */
  letterEmail: LetterEmailStatus | null;
}

const base = "/api/document-worker/v1";

/** Certificates, family letters and class scorecards generated when a session is finalized. */
export const documentsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSessionDocuments: builder.query<SessionDocumentsView, string>({
      query: (sessionId) => ({
        url: `${base}/sessions/${sessionId}/documents`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{
          documents: SessionDocument[];
          letterEmail?: LetterEmailStatus | null;
        }>,
      ) => ({
        documents: r.data.documents,
        letterEmail: r.data.letterEmail ?? null,
      }),
      providesTags: (_r, _e, sessionId) => [
        { type: "Documents", id: sessionId },
      ],
      keepUnusedDataFor: 600,
    }),
    getCertificateBatch: builder.query<SessionDocument, string>({
      query: (sessionId) => ({
        url: `${base}/sessions/${sessionId}/documents/certificate-batch`,
        method: "GET",
        // Renders a certificate for every student in the session inside the request.
        timeout: LONG_REQUEST_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<{ document: SessionDocument }>) =>
        r.data.document,
    }),
    getScorecard: builder.query<SessionDocument, string>({
      query: (sessionId) => ({
        url: `${base}/sessions/${sessionId}/documents/scorecard`,
        method: "GET",
        timeout: LONG_REQUEST_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<{ document: SessionDocument }>) =>
        r.data.document,
    }),
    getStudentDocuments: builder.query<SessionDocument[], string>({
      query: (studentId) => ({
        url: `${base}/students/${studentId}/documents`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<{ documents: SessionDocument[] }>) =>
        r.data.documents,
      providesTags: (_r, _e, studentId) => [
        { type: "Documents", id: `student:${studentId}` },
      ],
    }),
    getStudentVideo: builder.query<
      { url: string; key: string; createdAt: string },
      { studentId: string; testResultId: string; exerciseId: string }
    >({
      query: ({ studentId, testResultId, exerciseId }) => ({
        url: `${base}/students/${studentId}/videos?testResultId=${testResultId}&exerciseId=${encodeURIComponent(exerciseId)}`,
        method: "GET",
      }),
      transformResponse: (
        r: ApiResponse<{
          video: { url: string; key: string; createdAt: string };
        }>,
      ) => r.data.video,
    }),
    sendLetters: builder.mutation<{ queued: number; skipped: number }, string>({
      query: (sessionId) => ({
        url: `${base}/sessions/${sessionId}/documents/letters/send`,
        method: "POST",
      }),
      transformResponse: (
        r: ApiResponse<{ queued: number; skipped: number }>,
      ) => r.data,
      invalidatesTags: (_r, _e, sessionId) => [
        { type: "Documents", id: sessionId },
      ],
    }),
  }),
});

export const {
  useGetStudentVideoQuery,
  useGetSessionDocumentsQuery,
  useLazyGetCertificateBatchQuery,
  useLazyGetScorecardQuery,
  useGetStudentDocumentsQuery,
  useSendLettersMutation,
} = documentsApiSlice;
